import React, { useCallback, useState, createContext, useContext } from 'react';
export interface Selection {
  id: string;
  category: string;
  itemName: string;
  optionName: string;
  optionId: string;
  price: number;
  thumbnail?: string;
  addedAt: Date;
  isPackageItem?: boolean;
  patternId?: string;
  patternName?: string;
  patternPrice?: number;
}
interface PackageItem {
  itemName: string;
  optionId: string;
  optionName: string;
  price: number;
  thumbnail?: string;
}
interface ActivePackage {
  id: string;
  tier: string;
  category: string;
  price?: number;
  itemPrices?: Record<string, number>;
  items?: PackageItem[];
}
const MANAGED_ITEMS = ['Refrigerator', 'Range Oven', 'Dishwasher', 'Microwave', 'Countertop'];
interface SelectionsContextType {
  selections: Selection[];
  addSelection: (selection: Omit<Selection, 'id' | 'addedAt'>) => void;
  removeSelection: (id: string) => void;
  updateSelection: (id: string, optionName: string, optionId: string, price: number) => void;
  clearSelections: () => void;
  totalPrice: number;
  recentlyAdded: Selection | null;
  clearRecentlyAdded: () => void;
  activePackage: ActivePackage | null;
  setActivePackage: (pkg: ActivePackage) => void;
  clearActivePackage: () => void;
  isItemLockedByPackage: (category: string, itemName: string) => boolean;
  isItemOverridingPackage: (itemName: string) => boolean;
  getPackageItemPrice: (itemName: string) => number | undefined;
  // Package Override System
  allowOverrides: boolean;
  setAllowOverrides: (val: boolean) => void;
  packageOverrides: string[];
  hasConfirmedOverride: boolean;
  setHasConfirmedOverride: (val: boolean) => void;
  resetPackageOverrides: () => void;
  getOverrideCount: () => number;
  getOriginalPackageOption: (itemName: string) => {
    optionName: string;
    price: number;
    thumbnail?: string;
  } | undefined;
  setSelectionPattern: (itemName: string, pattern: {
    id: string;
    name: string;
    price: number;
  } | null) => void;
}
const SelectionsContext = createContext<SelectionsContextType | undefined>(undefined);
export function SelectionsProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [selections, setSelections] = useState<Selection[]>([]);
  const [recentlyAdded, setRecentlyAdded] = useState<Selection | null>(null);
  const [activePackage, setActivePackageState] = useState<ActivePackage | null>(null);
  // Package Override State
  const [allowOverrides, setAllowOverrides] = useState<boolean>(true); // Builder setting
  const [packageOverrides, setPackageOverrides] = useState<string[]>([]);
  const [originalPackageSelections, setOriginalPackageSelections] = useState<Record<string, Selection>>({});
  const [hasConfirmedOverride, setHasConfirmedOverride] = useState<boolean>(false);
  const addSelection = useCallback((selection: Omit<Selection, 'id' | 'addedAt'>) => {
    const newSelection: Selection = {
      ...selection,
      id: `${selection.category}-${selection.itemName}-${Date.now()}`,
      addedAt: new Date()
    };
    setSelections(prev => {
      const existingIndex = prev.findIndex(s => s.itemName === selection.itemName);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = newSelection;
        return updated;
      }
      return [...prev, newSelection];
    });
    // Track overrides
    if (activePackage && !selection.isPackageItem && (MANAGED_ITEMS.includes(selection.itemName) || activePackage.itemPrices?.[selection.itemName] !== undefined)) {
      setPackageOverrides(prev => {
        if (!prev.includes(selection.itemName)) {
          return [...prev, selection.itemName];
        }
        return prev;
      });
    }
    setRecentlyAdded(newSelection);
  }, [activePackage]);
  const setActivePackage = useCallback((pkg: ActivePackage) => {
    // Build package items and track their individual prices
    const packageItems: PackageItem[] = [];
    const addItem = (itemName: string, optionId: string, optionName: string, price: number, thumbnail?: string) => {
      packageItems.push({
        itemName,
        optionId,
        optionName,
        price,
        thumbnail
      });
    };
    // If items are provided directly (e.g. global packages), use them
    if (pkg.items && pkg.items.length > 0) {
      pkg.items.forEach(item => packageItems.push(item));
    } else if (pkg.tier === 'Standard') {
      addItem('Refrigerator', 'fridge-standard', 'Standard 30"', 0, 'https://cdn.magicpatterns.com/uploads/56xcQygN48ytC9vCWfbKsD/2a3e8f16-fd9a-4893-b4aa-68846e8eee8d.png');
      addItem('Range Oven', 'standard', 'Standard', 0, 'https://cdn.magicpatterns.com/uploads/ipGLrgJ5dnUHzeX467UrRX/99451023-beef-4830-b2e4-6b5c5bbc05fa.png');
      addItem('Dishwasher', 'dw-standard', 'Standard', 0, 'https://cdn.magicpatterns.com/uploads/8WnWtNRoYDXghwDDYKxMY1/3a7bf4b8-f2ce-43ab-812d-d2135155715f.png');
      addItem('Microwave', 'mw-standard', 'Standard', 0, 'https://cdn.magicpatterns.com/uploads/7J9vR4HKQ2tpt65jg7G16W/74bb9c06-756a-4000-8501-7b776559d1c5.png');
      addItem('Countertop', 'ct-laminate', 'Laminate', 0, 'https://cdn.magicpatterns.com/uploads/mVj6c7zkUqrRquwCmxWcL6/15d95c32-8149-4132-9785-f1bfe49bb891.png');
    } else if (pkg.tier === 'Level 1' || pkg.tier === 'Package 1') {
      addItem('Refrigerator', 'fridge-side-by-side', 'Side-by-Side', 1200, 'https://cdn.magicpatterns.com/uploads/4rsTQoSw8cboz4u88BHB6e/31666bba-be57-4389-96b2-845051abc30a.png');
      addItem('Range Oven', 'level-1', 'Stainless Steel', 1500, 'https://cdn.magicpatterns.com/uploads/7qRjsZBrCQMtsgwG6dR6aa/a12724ab-875b-49f0-82d7-cbbbc5f8d329.png');
      addItem('Dishwasher', 'dw-quiet', 'Ultra Quiet', 450, 'https://cdn.magicpatterns.com/uploads/9HgKuy26y4nFyrfLCdGjh9/43af1ca4-cf13-471b-bebe-8bd2e03fcbfb.png');
      addItem('Microwave', 'mw-over-range', 'Over-the-Range', 350, 'https://cdn.magicpatterns.com/uploads/raVYgQuqvg9FTcFXiVoWdF/d1779e5a-0f26-4813-b30b-c76d034a46e4.png');
      addItem('Countertop', 'ct-butcher-block', 'Butcher Block', 3200, 'https://cdn.magicpatterns.com/uploads/f1hzbo3o3QLATPgnhLZGui/c9b16336-b6de-4ae1-8665-58f652453ea2.png');
    } else if (pkg.tier === 'Level 2' || pkg.tier === 'Package 2') {
      addItem('Refrigerator', 'fridge-french-door', 'French Door 36"', 1800, 'https://cdn.magicpatterns.com/uploads/sj8L76yNDFjCGFZWdgGY4N/9d9925e9-c3ea-4a0d-80fb-ffcfee4e866e.png');
      addItem('Range Oven', 'level-2', 'Professional', 2500, 'https://cdn.magicpatterns.com/uploads/rBmiA4HJkMsYLa3DUGGUeP/a38be756-541f-4f67-b58f-3e7cff68d49c.png');
      addItem('Dishwasher', 'dw-third-rack', 'Third Rack', 650, 'https://cdn.magicpatterns.com/uploads/trdsaseMYftX5ReXeRVWmo/728deced-e8d7-48dc-a7f2-8c641afae38b.png');
      addItem('Microwave', 'mw-built-in', 'Built-In', 800, 'https://cdn.magicpatterns.com/uploads/4o8ktMaBmPjywuSkMVi4Um/e2e961b3-6f11-40d0-b1be-f04ad41d6c36.png');
      addItem('Countertop', 'ct-granite', 'Granite', 4500, 'https://cdn.magicpatterns.com/uploads/kpH5AVoc585CmkhtFwT2gu/bb2e92e2-95c2-4917-a8f9-e317d073068e.png');
    } else if (pkg.tier === 'Level 3' || pkg.tier === 'Package 3') {
      addItem('Refrigerator', 'fridge-counter-depth', 'Counter Depth', 2400, 'https://cdn.magicpatterns.com/uploads/8uMrohCuZZGCZe6mp1Ebh5/bdab08d5-af5f-4768-afba-d5b3c4e4d4f4.png');
      addItem('Range Oven', 'level-3', 'Chef Series', 3500, 'https://cdn.magicpatterns.com/uploads/i3anGUuRyR4DYNoRawPTEF/ba5cadb3-2c6f-4c61-b770-e245581bd9ca.png');
      addItem('Dishwasher', 'dw-panel-ready', 'Panel Ready', 850, 'https://cdn.magicpatterns.com/uploads/gXch4QkMwxNQ9Gf4G5EAdJ/b394dd48-02c4-4c34-a060-d76f1604fa7c.png');
      addItem('Microwave', 'mw-drawer', 'Drawer Style', 1200, 'https://cdn.magicpatterns.com/uploads/9g5j9FPCgRFZUjU4MVxTD6/dcf7809d-faed-42b2-844a-3dab62702865.png');
      addItem('Countertop', 'ct-quartz', 'Quartz', 5500, 'https://cdn.magicpatterns.com/uploads/hA4ppMCeeEnfUr8DdemFhK/80b42e57-750f-436c-8070-ab27d0716845.png');
    } else if (pkg.tier === 'Level 4' || pkg.tier === 'Package 4') {
      addItem('Refrigerator', 'fridge-smart', 'Smart Hub', 3500, 'https://cdn.magicpatterns.com/uploads/rWjZv6Mm4vUpRox9hUrK2K/92c595c0-b3df-4f96-84f9-414aad4eea5b.png');
      addItem('Range Oven', 'level-4', 'Commercial Grade', 4500, 'https://cdn.magicpatterns.com/uploads/j59ZjLMVTNqtgapFimQc1E/08ea5d1f-e4b6-4d72-8184-56c394b9cb43.png');
      addItem('Dishwasher', 'dw-smart', 'Smart Connect', 1200, 'https://cdn.magicpatterns.com/uploads/iQkCQnTBXVuPRJZw5CWi23/a3b5acff-a1c0-4442-84b1-286862e88499.png');
      addItem('Microwave', 'mw-drawer', 'Drawer Style', 1200, 'https://cdn.magicpatterns.com/uploads/9g5j9FPCgRFZUjU4MVxTD6/dcf7809d-faed-42b2-844a-3dab62702865.png');
      addItem('Countertop', 'ct-marble', 'Marble', 8500, 'https://cdn.magicpatterns.com/uploads/xefSXa2PcHLjXAgiprF9C2/94335722-d546-4e85-83cf-af86687637c9.png');
    }
    // Build itemPrices map for delta calculations
    const itemPrices: Record<string, number> = {};
    packageItems.forEach(item => {
      itemPrices[item.itemName] = item.price;
    });
    // Store package with item prices
    setActivePackageState({
      ...pkg,
      itemPrices
    });
    // Reset overrides
    setPackageOverrides([]);
    setHasConfirmedOverride(false);
    const originalSelections: Record<string, Selection> = {};
    // Apply all selections as package items
    packageItems.forEach(item => {
      const selectionData = {
        category: pkg.category,
        itemName: item.itemName,
        optionName: item.optionName,
        optionId: item.optionId,
        price: item.price,
        thumbnail: item.thumbnail,
        isPackageItem: true
      };
      originalSelections[item.itemName] = {
        ...selectionData,
        id: `${selectionData.category}-${selectionData.itemName}-${Date.now()}`,
        addedAt: new Date()
      };
      addSelection(selectionData);
    });
    setOriginalPackageSelections(originalSelections);
    // Set recentlyAdded to the package itself for the toast
    setRecentlyAdded({
      id: `package-${pkg.id}-${Date.now()}`,
      category: pkg.category,
      itemName: pkg.category === 'Global' ? 'Global Package' : 'Kitchen Package',
      optionName: pkg.tier,
      optionId: pkg.id,
      price: pkg.price ?? 0,
      addedAt: new Date(),
      isPackageItem: true
    });
  }, [addSelection]);
  const clearActivePackage = useCallback(() => {
    setActivePackageState(null);
    setPackageOverrides([]);
    setOriginalPackageSelections({});
    setHasConfirmedOverride(false);
    // Keep selections but mark them as not package items
    setSelections(prev => prev.map(s => ({
      ...s,
      isPackageItem: false
    })));
  }, []);
  const resetPackageOverrides = useCallback(() => {
    if (!activePackage) return;
    setSelections(prev => {
      const updated = [...prev];
      packageOverrides.forEach(itemName => {
        const originalSelection = originalPackageSelections[itemName];
        if (originalSelection) {
          const index = updated.findIndex(s => s.itemName === itemName);
          if (index >= 0) {
            updated[index] = {
              ...originalSelection,
              addedAt: new Date()
            };
          } else {
            updated.push({
              ...originalSelection,
              addedAt: new Date()
            });
          }
        }
      });
      return updated;
    });
    setPackageOverrides([]);
  }, [activePackage, packageOverrides, originalPackageSelections]);
  const getOverrideCount = useCallback(() => {
    return packageOverrides.length;
  }, [packageOverrides]);
  const getOriginalPackageOption = useCallback((itemName: string) => {
    const original = originalPackageSelections[itemName];
    if (!original) return undefined;
    return {
      optionName: original.optionName,
      price: original.price,
      thumbnail: original.thumbnail
    };
  }, [originalPackageSelections]);
  const setSelectionPattern = useCallback((itemName: string, pattern: {
    id: string;
    name: string;
    price: number;
  } | null) => {
    setSelections(prev => prev.map(s => s.itemName === itemName ? {
      ...s,
      patternId: pattern?.id,
      patternName: pattern?.name,
      patternPrice: pattern?.price ?? 0
    } : s));
  }, []);
  const isItemLockedByPackage = useCallback((_category: string, itemName: string) => {
    return !allowOverrides && activePackage?.itemPrices?.[itemName] !== undefined;
  }, [allowOverrides, activePackage]);
  // Check if an item is overriding a package item
  const isItemOverridingPackage = useCallback((itemName: string) => {
    return packageOverrides.includes(itemName);
  }, [packageOverrides]);
  // Get the original package price for an item (for delta display)
  const getPackageItemPrice = useCallback((itemName: string): number | undefined => {
    if (!activePackage || !activePackage.itemPrices) return undefined;
    return activePackage.itemPrices[itemName];
  }, [activePackage]);
  const removeSelection = useCallback((id: string) => {
    setSelections(prev => prev.filter(s => s.id !== id));
  }, []);
  const updateSelection = useCallback((id: string, optionName: string, optionId: string, price: number) => {
    setSelections(prev => prev.map(s => s.id === id ? {
      ...s,
      optionName,
      optionId,
      price
    } : s));
  }, []);
  const clearSelections = useCallback(() => {
    setSelections([]);
    setActivePackageState(null);
    setPackageOverrides([]);
    setOriginalPackageSelections({});
    setHasConfirmedOverride(false);
  }, []);
  const clearRecentlyAdded = useCallback(() => {
    setRecentlyAdded(null);
  }, []);
  // Total price calculation:
  // If package is active: packagePrice + deltas for overrides + independent items
  const totalPrice = (() => {
    if (activePackage && activePackage.price !== undefined && activePackage.itemPrices) {
      let total = activePackage.price;
      selections.forEach(s => {
        if (s.isPackageItem) {
          // Covered by the package price — no additional cost
          return;
        }
        if ((MANAGED_ITEMS.includes(s.itemName) || activePackage.itemPrices![s.itemName] !== undefined) && activePackage.itemPrices![s.itemName] !== undefined) {
          // This is an override — add the delta (new price - original package item price)
          const delta = s.price - activePackage.itemPrices![s.itemName];
          total += Math.max(0, delta); // Don't subtract if downgrading
        } else {
          // Independent item, not part of package
          total += s.price;
        }
      });
      return total;
    }
    return selections.reduce((sum, s) => sum + s.price + (s.patternPrice ?? 0), 0);
  })();
  return <SelectionsContext.Provider value={{
    selections,
    addSelection,
    removeSelection,
    updateSelection,
    clearSelections,
    totalPrice,
    recentlyAdded,
    clearRecentlyAdded,
    activePackage,
    setActivePackage,
    clearActivePackage,
    isItemLockedByPackage,
    isItemOverridingPackage,
    getPackageItemPrice,
    allowOverrides,
    setAllowOverrides,
    packageOverrides,
    hasConfirmedOverride,
    setHasConfirmedOverride,
    resetPackageOverrides,
    getOverrideCount,
    getOriginalPackageOption,
    setSelectionPattern
  }} data-id="element-1807">
      {children}
    </SelectionsContext.Provider>;
}
export function useSelections() {
  const context = useContext(SelectionsContext);
  if (context === undefined) {
    throw new Error('useSelections must be used within a SelectionsProvider');
  }
  return context;
}